
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from "recharts";
import { TrendingUp, TrendingDown, Calendar, Database, AlertCircle } from "lucide-react";
import { PricingStandard } from "../../../types";

interface HistoricalAnalysisProps {
  pricingStandards: PricingStandard[];
}

const HistoricalAnalysis = ({ pricingStandards }: HistoricalAnalysisProps) => {
  const [selectedItem, setSelectedItem] = useState<string>("concrete");
  const [timeRange, setTimeRange] = useState<string>("12m");

  // Sample historical data
  const historicalData = {
    concrete: [
      { month: "2023-02", price: 17500, projects: 12, variance: 8.2 },
      { month: "2023-03", price: 17800, projects: 15, variance: 9.1 },
      { month: "2023-04", price: 18200, projects: 18, variance: 10.5 },
      { month: "2023-05", price: 18500, projects: 22, variance: 11.2 },
      { month: "2023-06", price: 18800, projects: 19, variance: 12.1 },
      { month: "2023-07", price: 19000, projects: 24, variance: 12.8 },
      { month: "2023-08", price: 19200, projects: 21, variance: 13.2 },
      { month: "2023-09", price: 19100, projects: 26, variance: 12.9 },
      { month: "2023-10", price: 19300, projects: 28, variance: 13.5 },
      { month: "2023-11", price: 19500, projects: 25, variance: 14.1 },
      { month: "2023-12", price: 19200, projects: 20, variance: 13.8 },
      { month: "2024-01", price: 19000, projects: 23, variance: 12.5 }
    ],
    steel: [
      { month: "2023-02", price: 850000, projects: 8, variance: 15.2 },
      { month: "2023-03", price: 870000, projects: 10, variance: 16.1 },
      { month: "2023-04", price: 920000, projects: 12, variance: 18.5 },
      { month: "2023-05", price: 950000, projects: 14, variance: 19.2 },
      { month: "2023-06", price: 980000, projects: 11, variance: 20.1 },
      { month: "2023-07", price: 1020000, projects: 15, variance: 21.8 },
      { month: "2023-08", price: 1050000, projects: 13, variance: 22.2 },
      { month: "2023-09", price: 1030000, projects: 16, variance: 21.9 },
      { month: "2023-10", price: 1080000, projects: 18, variance: 23.5 },
      { month: "2023-11", price: 1100000, projects: 17, variance: 24.1 },
      { month: "2023-12", price: 1070000, projects: 14, variance: 23.8 },
      { month: "2024-01", price: 950000, projects: 16, variance: 18.2 }
    ]
  };

  const currentData = historicalData[selectedItem as keyof typeof historicalData] || historicalData.concrete;
  
  const calculateTrend = (data: any[]) => {
    if (data.length < 2) return { direction: 'stable', percentage: 0 };
    
    const firstPrice = data[0].price;
    const lastPrice = data[data.length - 1].price;
    const percentage = ((lastPrice - firstPrice) / firstPrice) * 100;
    
    return {
      direction: percentage > 2 ? 'increasing' : percentage < -2 ? 'decreasing' : 'stable',
      percentage: Math.abs(percentage)
    };
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const trend = calculateTrend(currentData);
  const avgPrice = currentData.reduce((sum, item) => sum + item.price, 0) / currentData.length;
  const totalProjects = currentData.reduce((sum, item) => sum + item.projects, 0);
  const avgVariance = currentData.reduce((sum, item) => sum + item.variance, 0) / currentData.length;

  // Forecast next 3 months based on trend
  const generateForecast = () => {
    const lastMonth = currentData[currentData.length - 1];
    const monthlyGrowth = trend.percentage / 12; // Convert annual to monthly
    const direction = trend.direction === 'increasing' ? 1 : trend.direction === 'decreasing' ? -1 : 0;
    
    return [1, 2, 3].map(month => ({
      month: `2024-0${1 + month}`,
      price: Math.round(lastMonth.price * (1 + (direction * monthlyGrowth * month / 100))),
      forecast: true
    }));
  };

  const forecast = generateForecast();

  return (
    <div className="space-y-6">
      {/* Header and Controls */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Historical Price Analysis</h3>
          <p className="text-gray-600">Track price trends and predict future costs using historical data</p>
        </div>
        <div className="flex space-x-2">
          <Select value={selectedItem} onValueChange={setSelectedItem}>
            <SelectTrigger className="w-48">
              <Database className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="concrete">Concrete Grade 30</SelectItem>
              <SelectItem value="steel">Steel Reinforcement</SelectItem>
            </SelectContent>
          </Select>
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-32">
              <Calendar className="h-4 w-4 mr-2" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="6m">6 Months</SelectItem>
              <SelectItem value="12m">12 Months</SelectItem>
              <SelectItem value="24m">24 Months</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Average Price</CardTitle>
            <Database className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(avgPrice)}</div>
            <p className="text-xs text-muted-foreground">12-month average</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Price Trend</CardTitle>
            {trend.direction === 'increasing' ? 
              <TrendingUp className="h-4 w-4 text-red-500" /> :
              trend.direction === 'decreasing' ?
              <TrendingDown className="h-4 w-4 text-green-500" /> :
              <TrendingUp className="h-4 w-4 text-gray-500" />
            }
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{trend.percentage.toFixed(1)}%</div>
            <p className={`text-xs ${
              trend.direction === 'increasing' ? 'text-red-600' : 
              trend.direction === 'decreasing' ? 'text-green-600' : 'text-gray-600'
            }`}>
              {trend.direction} trend
            </p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Data Points</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalProjects}</div>
            <p className="text-xs text-muted-foreground">Projects analyzed</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg Variance</CardTitle>
            <AlertCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{avgVariance.toFixed(1)}%</div>
            <p className="text-xs text-muted-foreground">Price volatility</p>
          </CardContent>
        </Card>
      </div>

      {/* Price Trend Chart */}
      <Card>
        <CardHeader>
          <CardTitle>Price Trend Analysis</CardTitle>
          <CardDescription>
            Historical prices with forecast projection
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={[...currentData, ...forecast]}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="month" 
                tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short', year: '2-digit' })}
              />
              <YAxis 
                tickFormatter={(value) => formatCurrency(value)}
              />
              <Tooltip 
                formatter={(value: any, name: any, props: any) => [
                  formatCurrency(Number(value)),
                  props.payload.forecast ? 'Forecast' : 'Actual'
                ]}
                labelFormatter={(label) => new Date(label).toLocaleDateString('en-US', { 
                  month: 'long', 
                  year: 'numeric' 
                })}
              />
              <Line 
                type="monotone" 
                dataKey="price" 
                stroke="#3b82f6" 
                strokeWidth={2}
                dot={{ r: 4 }}
                connectNulls={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      {/* Project Volume and Variance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Project Volume Trends</CardTitle>
            <CardDescription>Number of projects by month</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={currentData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="month" 
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short' })}
                />
                <YAxis />
                <Tooltip 
                  formatter={(value) => [`${value} projects`, 'Projects']}
                  labelFormatter={(label) => new Date(label).toLocaleDateString('en-US', { 
                    month: 'long', 
                    year: 'numeric' 
                  })}
                />
                <Bar 
                  dataKey="projects" 
                  fill="#10b981"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Price Variance Over Time</CardTitle>
            <CardDescription>Market volatility indicators</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={currentData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="month" 
                  tickFormatter={(value) => new Date(value).toLocaleDateString('en-US', { month: 'short' })}
                />
                <YAxis />
                <Tooltip 
                  formatter={(value) => [`${Number(value).toFixed(1)}%`, 'Variance']}
                />
                <Line 
                  type="monotone" 
                  dataKey="variance" 
                  stroke="#ef4444" 
                  strokeWidth={2}
                  dot={{ r: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Insights and Forecasting */}
      <Card>
        <CardHeader>
          <CardTitle>Market Insights & Forecasting</CardTitle>
          <CardDescription>Key findings and price predictions based on historical analysis</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-medium mb-4">Key Insights</h4>
              <div className="space-y-3">
                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <TrendingUp className="h-4 w-4 text-blue-600" />
                    <span className="text-sm font-medium text-blue-700">Price Trend</span>
                  </div>
                  <p className="text-xs text-blue-600 mt-1">
                    {selectedItem === 'concrete' ? 
                      'Concrete prices show moderate stability with seasonal fluctuations' :
                      'Steel prices demonstrate high volatility due to global market factors'
                    }
                  </p>
                </div>
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Database className="h-4 w-4 text-green-600" />
                    <span className="text-sm font-medium text-green-700">Data Quality</span>
                  </div>
                  <p className="text-xs text-green-600 mt-1">
                    High confidence level with {totalProjects} data points across 12 months
                  </p>
                </div>
                <div className="p-3 bg-orange-50 border border-orange-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <AlertCircle className="h-4 w-4 text-orange-600" />
                    <span className="text-sm font-medium text-orange-700">Risk Factors</span>
                  </div>
                  <p className="text-xs text-orange-600 mt-1">
                    {avgVariance > 15 ? 
                      'High volatility requires frequent rate updates and risk buffers' :
                      'Moderate volatility allows for stable pricing strategies'
                    }
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-medium mb-4">3-Month Forecast</h4>
              <div className="space-y-3">
                {forecast.map((month, index) => (
                  <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                    <div>
                      <span className="font-medium">
                        {new Date(month.month).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </span>
                      <div className="text-sm text-gray-600">Projected</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold">{formatCurrency(month.price)}</div>
                      <Badge variant="outline" className="text-xs">
                        {trend.direction === 'increasing' ? '+' : trend.direction === 'decreasing' ? '-' : ''}
                        {(trend.percentage * (index + 1) / 12).toFixed(1)}%
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="h-4 w-4 text-yellow-600" />
                  <span className="text-sm font-medium text-yellow-700">Forecast Disclaimer</span>
                </div>
                <p className="text-xs text-yellow-600 mt-1">
                  Predictions based on historical trends. Actual prices may vary due to market conditions.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default HistoricalAnalysis;

